import { FullScreenPage } from "@/components/full-screen-page";
import { toError } from "@/lib/errors";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Stack } from "@/components/ui/Stack";

interface FullPageErrorProps {
  error: unknown;
}

// Several public capability-URL endpoints carry a live credential in a
// "token" query parameter (e.g. skills.getShared) or as the path segment
// after /shared/skills/. Error messages and request URLs shown in this
// debug box must never display them verbatim.
function redactTokens(text: string): string {
  return text
    .replace(/([?&]token=)[^&\s]+/g, "$1REDACTED")
    .replace(/(\/shared\/skills\/)[^/?\s]+/g, "$1REDACTED");
}

export function FullPageError({
  error: rawError,
}: FullPageErrorProps): JSX.Element {
  const error = toError(rawError);
  return (
    <FullScreenPage contentClassName="max-w-md text-center">
      <Stack gap={3} align="center">
        <Stack direction="horizontal" gap={2} align="center">
          <Icon name="circle-alert" className="text-destructive h-5 w-5" />
          <h2 className="text-lg font-medium">Something went wrong</h2>
        </Stack>

        <p className="text-muted-foreground text-sm">
          An unexpected error occurred. Try reloading the page or contact
          support if the problem persists.
        </p>

        <div className="bg-muted w-full p-3">
          <p className="text-muted-foreground font-mono text-xs break-all">
            {redactTokens(error.message)}
          </p>
          {"rawResponse" in error &&
            error.rawResponse instanceof Response &&
            error.rawResponse.url && (
              <p className="text-muted-foreground mt-2 font-mono text-xs break-all">
                {redactTokens(error.rawResponse.url)}
              </p>
            )}
        </div>
      </Stack>

      <Stack direction="horizontal" gap={2}>
        <Button variant="brand" onClick={() => window.location.reload()}>
          <Button.LeftIcon>
            <Icon name="rotate-ccw" className="h-4 w-4" />
          </Button.LeftIcon>
          <Button.Text>Reload page</Button.Text>
        </Button>
        <Button asChild variant="secondary">
          <a href="/">Go to home</a>
        </Button>
      </Stack>
    </FullScreenPage>
  );
}
